/**
 * MADEKTA — motion.js
 * Aparición progresiva, trazos técnicos y parallax moderado.
 * Se desactiva por completo con prefers-reduced-motion o desde el tema.
 */
import { prefersReducedMotion } from '@madekta/core';

const root = document.documentElement;
const enabled = root.dataset.animations === 'true' && !prefersReducedMotion();

function prepareDrawings(scope) {
  scope.querySelectorAll('[data-draw] :is(path, line, rect, circle)').forEach((shape) => {
    if (typeof shape.getTotalLength === 'function') {
      shape.style.setProperty('--path-length', String(Math.ceil(shape.getTotalLength())));
    }
  });
}

function indexGroups(scope) {
  scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
    group
      .querySelectorAll(':scope > [data-reveal]')
      .forEach((item, i) => item.style.setProperty('--reveal-index', String(i % 8)));
  });
}

let observer;
const pending = new Set();

function reveal(el) {
  el.classList.add('is-revealed');
  observer.unobserve(el);
  pending.delete(el);
}

/* IntersectionObserver solo avisa cuando un elemento cruza el viewport. Un salto
   de scroll (recarga con la posición guardada, tecla Fin, ancla, scroll rápido)
   lo lleva de abajo a arriba sin cruzarlo y quedaría oculto: este barrido
   muestra todo lo que ya se alcanzó. */
let sweeping = false;
function sweep() {
  sweeping = false;
  const limit = window.innerHeight * 0.92;
  const reached = [...pending].filter((el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < limit && (rect.width > 0 || rect.height > 0);
  });
  reached.forEach(reveal);
}
function requestSweep() {
  if (sweeping || !pending.size) return;
  sweeping = true;
  requestAnimationFrame(sweep);
}

function observe(scope = document) {
  if (!enabled) return;
  prepareDrawings(scope);
  indexGroups(scope);
  scope.querySelectorAll('[data-reveal]:not(.is-revealed), [data-draw]:not(.is-revealed)').forEach((el) => {
    pending.add(el);
    observer.observe(el);
  });
  requestSweep();
}

function setupParallax() {
  const items = [...document.querySelectorAll('[data-parallax]')];
  if (!items.length) return;
  let ticking = false;
  const visible = new Set();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => (entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)));
  });
  items.forEach((item) => io.observe(item));

  const update = () => {
    const vh = window.innerHeight;
    visible.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const speed = Number(el.dataset.parallax) || 0.08;
      const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
      el.style.setProperty('--parallax', `${offset.toFixed(1)}px`);
    });
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true },
  );
  update();
}

if (enabled) {
  root.classList.add('js-motion');
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 },
  );
  observe();
  window.addEventListener('scroll', requestSweep, { passive: true });
  window.addEventListener('resize', requestSweep, { passive: true });
  window.addEventListener('pageshow', requestSweep);
  if (window.matchMedia('(min-width: 768px)').matches) setupParallax();

  /* Contenido inyectado (editor de temas, filtros, carga progresiva). */
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
  document.addEventListener('madekta:content-replaced', (event) => observe(event.target));
}
